import type { FlowExtensionErrorCode } from './codes'

// Base class for all flow-extension runtime errors. Mirrors the console's `ApiError` shape (statusCode +
// type/code + message + details) so the server can map it straight onto an HTTP response, while staying
// self-contained (the runner never imports the console workspace).

export interface FlowExtensionErrorJSON {
  name: string
  code: FlowExtensionErrorCode
  statusCode: number
  message: string
  details?: unknown
}

export class FlowExtensionError<DetailsT = unknown> extends Error {
  // Discriminant that survives structured-clone / JSON across the worker boundary, where `instanceof` fails.
  readonly isFlowExtensionError = true

  constructor(
    public readonly code: FlowExtensionErrorCode,
    public readonly statusCode: number,
    message: string,
    public readonly details?: DetailsT
  ) {
    super(message)
    this.name = this.constructor.name

    // Maintains a proper stack trace for where the error was thrown (V8 only).
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor)
    }
  }

  toJSON(): FlowExtensionErrorJSON {
    return {
      name: this.name,
      code: this.code,
      statusCode: this.statusCode,
      message: this.message,
      details: this.details,
    }
  }
}

// True for a FlowExtensionError instance, or a plain object carrying the discriminant (e.g. after the error
// crossed the worker IPC boundary and lost its prototype).
export function isFlowExtensionError(value: unknown): value is FlowExtensionError {
  if (value instanceof FlowExtensionError) {
    return true
  }

  return typeof value === 'object' &&
    value !== null &&
    (value as { isFlowExtensionError?: unknown }).isFlowExtensionError === true
}
